import { useMemo, useState } from 'react';
import { addDays } from '@fluentui/react-calendar-compat';
import { useTranslation } from 'react-i18next';
import { metricsDateFormat } from '../../../../../store/features/reportingDashboard/reportingDashboardSlice';
import { formatDate } from '../../../../../i18n';
import {
    DateRangeValidationMessages,
    DateRangeValidationResult,
    getDateRangeValidationMessage,
    validateDateRange,
} from '../../../../../utils/dateRangeValidation';

const maxWeekRange = 6;
const maxDayRange = maxWeekRange * 7;

export interface DateRangeConstraints {
    min: Date;
    max: Date;
}

interface UseDateRangeConstraintsResult {
    startDate: Date | null;
    setStartDate: (date: Date | null) => void;
    endDate: Date | null;
    setEndDate: (date: Date | null) => void;
    dateConstraints: DateRangeConstraints;
    isValid: boolean;
    validationMessage: string | null;
}

export const useDateRangeConstraints = (
    intialStartDate: Date | null,
    intialEndDate: Date | null
): UseDateRangeConstraintsResult => {
    const { t } = useTranslation();

    const yesterday = useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return addDays(now, -1);
    }, []);

    const [startDate, setStartDate] = useState(
        intialStartDate ? new Date(intialStartDate) : null
    );
    const [endDate, setEndDate] = useState(
        intialEndDate ? new Date(intialEndDate) : null
    );

    const dateConstraints = useMemo<DateRangeConstraints>(
        () => ({
            min: new Date(2020, 0, 1),
            max: yesterday,
        }),
        [yesterday]
    );

    const validationMessages = useMemo<DateRangeValidationMessages>(
        () => ({
            startAfterEnd: t('ReportingDashboard_Error_StartBeforeEnd'),
            outOfRange: t('ReportingDashboard_Error_DateConstraints')
                .replace('{min}', formatDate(dateConstraints.min, metricsDateFormat))
                .replace('{max}', formatDate(dateConstraints.max, metricsDateFormat)),
            maxDaySpan: t('ReportingDashboard_Error_MaxWeekRange').replace(
                '{range}',
                maxWeekRange.toString()
            ),
        }),
        [dateConstraints.max, dateConstraints.min, t]
    );

    const validationResult: DateRangeValidationResult = useMemo(
        () =>
            validateDateRange(startDate, endDate, {
                min: dateConstraints.min,
                max: dateConstraints.max,
                maxDaySpan: maxDayRange,
                includeEndDate: true,
            }),
        [startDate, endDate, dateConstraints]
    );

    const validationMessage = useMemo(
        () => getDateRangeValidationMessage(validationResult, validationMessages),
        [validationMessages, validationResult]
    );

    return {
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        dateConstraints,
        isValid: validationResult.isValid || validationResult.reason === 'missing',
        validationMessage,
    };
};
